import { InfiniteData } from '@reduxjs/toolkit/query';
import { v4 } from 'uuid';

import {
    IAutomaticAssistantMessage,
    IGetMessagesV2Response,
    IInputEvent,
    IMessage,
    IMessageToolCallResult,
    IToolCallEvent,
    IToolResultEvent,
    MessageRole,
    MessageStageType,
    StreamStatus,
} from '../../types';

import { GetMessagesPaginateParams } from './message-endpoints';

export const onAutomaticAssistantMessageUpdater = (
    draft: GetMessagesDraft,
    event: IAutomaticAssistantMessage
): void => {
    if (!draft) {
        return;
    }

    const { turn_id, content } = event;

    const message = {
        role: MessageRole.ASSISTANT,
        status: StreamStatus.DONE,
        content,
        turn_id,
        message_id: v4(),
    };

    // in the ideal case it should happen on the first page of draft.pages
    for (const page of draft.pages) {
        const idx = page.messages.findIndex((m) => m.is_placeholder);

        if (idx !== -1) {
            page.messages.splice(Math.max(0, idx - 1), 0, message);

            break;
        }
    }
};

export const onToolCallEventUpdater = (messagePlaceholder: IMessage, event: IToolCallEvent): void => {
    messagePlaceholder.stage = MessageStageType.TOOL_CALLS;
    messagePlaceholder.status = event.status;
    messagePlaceholder.message_id = event.message_id;
    messagePlaceholder.tool_calls = Array.from(messagePlaceholder?.tool_calls ?? []);

    messagePlaceholder.tool_calls.push({
        function_name: event.function_name,
        function_args: event.arguments,
        tool_call_id: event.tool_call_id,
        tool_call_succeeded: true,
    });
};

export const onInputEventUpdater = (messagePlaceholder: IMessage, event: IInputEvent): void => {
    messagePlaceholder.stage = MessageStageType.GATHERING_INPUT;
    messagePlaceholder.status = event.status;
    messagePlaceholder.message_id = event.message_id;
    messagePlaceholder.input = event.raw_input;
};

export const onToolResultEventUpdater = (messagePlaceholder: IMessage, event: IToolResultEvent): void => {
    messagePlaceholder.stage = MessageStageType.TOOL_RESULTS;
    messagePlaceholder.status = event.status;
    messagePlaceholder.message_id = event.message_id;

    for (const tool_call of messagePlaceholder?.tool_calls ?? []) {
        if (tool_call.tool_call_id === event.tool_call_id) {
            tool_call.tool_call_succeeded = event.tool_call_succeeded;
            break;
        }
    }

    const toolsCallsResults = messagePlaceholder?.tool_calls_results ?? [];

    const exists = toolsCallsResults.find((callResult) => callResult.tool_call_id === event.tool_call_id);

    if (exists) {
        exists.tool_call_succeeded = event.tool_call_succeeded;
        exists.result = event.result;
    } else {
        // because we are set the `function_name` and `result` the TS loses the context of the type for `result`
        // so we need to cast it to `IMessageToolCallResult`/
        toolsCallsResults.push({
            function_name: event.function_name,
            tool_call_id: event.tool_call_id,
            tool_call_succeeded: event.tool_call_succeeded,
            result: event?.result,
        } as IMessageToolCallResult);
    }

    messagePlaceholder.tool_calls_results = toolsCallsResults;
};

type GetMessagesDraft = InfiniteData<IGetMessagesV2Response, GetMessagesPaginateParams> | undefined;

/**
 * Inserts messages to the beginning of the conversation
 *
 * @param messagesDraft
 * @param messages
 */
export const insertMessages = (messagesDraft: GetMessagesDraft, messages: IMessage[]) => {
    if (!messagesDraft) {
        return;
    }

    let firstPage = messagesDraft.pages[0];

    if (!firstPage) {
        firstPage = {
            messages,
            cursor: null,
        };
    } else {
        firstPage.messages.push(...messages);
    }

    messagesDraft.pages[0] = firstPage;
};
